/**
 * Eine Apartment-Tageszelle.
 *
 * Bearbeitbare Zellen sind Buttons — Auswahl per Klick, Ziehen, Shift (verlaengern) und
 * Strg/Cmd (weiterer Zeitraum). Ohne `Edit`-Privileg bleibt dieselbe Zelle ein stiller Container;
 * es wird kein deaktivierter Control gerendert.
 */
import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';
import { DoubleBookedMark } from '@/features/properties/flat-family/DoubleBookedMark';
import { cn } from '@/shared/lib/cn';
import { CloseoutMark, DayCellBody, cellTitle, stateLabel } from './DayCellBody';
import { PublicationMark, automationText } from '../automation/AutomationMarks';
import type { AutomationCell } from '../automation/automationModel';
import type { DayCellModel } from './calendarModel';
import type { SelectionMode } from './rangeSelectionModel';

export interface DayCellProps {
  cell: DayCellModel;
  currency: string;
  isToday: boolean;
  isWeekend: boolean;
  covered: boolean;
  selectionIndex: number;
  /** Preisautomatik-Schicht (nur Admin/Staff): Ziel und Übertragungsmarke. */
  automation?: AutomationCell | null;
  onBegin?: (date: string, mode: SelectionMode, immediate?: boolean) => void;
  onExtend?: (date: string) => void;
}

export function DayCell({
  cell,
  currency,
  isToday,
  isWeekend,
  covered,
  selectionIndex,
  automation = null,
  onBegin,
  onExtend,
}: DayCellProps) {
  const selected = selectionIndex >= 0;
  const className = cellClassName({ cell, isToday, isWeekend, selected, interactive: !!onBegin });
  const style = { gridColumn: cell.index + 2, gridRow: 1 } as const;
  const label = `${cell.day.date}: ${stateLabel(cell)}`;
  const title = cellTitle(cell, currency, automationText(automation));
  const content: ReactNode = (
    <>
      {selected && <RangeChip index={selectionIndex} />}
      {covered ? null : <DayCellBody cell={cell} currency={currency} automation={automation} />}
      {!covered && <PublicationMark cell={automation} />}
      {cell.closeout && !covered && <CloseoutMark closeout={cell.closeout} />}
      {cell.kind === 'doubleBooked' && (
        <DoubleBookedMark className="absolute inset-x-1.5 bottom-1" />
      )}
    </>
  );

  if (!onBegin) {
    return (
      <div style={style} aria-label={label} title={title} className={className}>
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      style={style}
      aria-label={label}
      title={title}
      aria-pressed={selected}
      className={className}
      onPointerDown={(event: PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onBegin(cell.day.date, selectionModeFrom(event));
      }}
      onPointerEnter={() => onExtend?.(cell.day.date)}
      onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onBegin(cell.day.date, keyboardMode(event), true);
      }}
    >
      {content}
    </button>
  );
}

function cellClassName({
  cell,
  isToday,
  isWeekend,
  selected,
  interactive,
}: {
  cell: DayCellModel;
  isToday: boolean;
  isWeekend: boolean;
  selected: boolean;
  interactive: boolean;
}): string {
  return cn(
    'border-hairline relative flex select-none flex-col items-center justify-center gap-0.5 border-l px-1',
    isWeekend && 'bg-paper/70',
    isToday && 'bg-forest/[0.06]',
    cell.kind === 'blocked' && BLOCKED_STRIPES,
    cell.kind === 'closedByFamily' && FAMILY_STRIPES,
    interactive && !selected && 'hover:bg-forest-soft/70 transition-colors',
    selected && 'bg-forest-soft',
  );
}

const BLOCKED_STRIPES =
  'bg-[repeating-linear-gradient(135deg,var(--color-sand)_0_6px,var(--color-paper)_6px_12px)]';
const FAMILY_STRIPES =
  'bg-[repeating-linear-gradient(135deg,var(--color-card-alt)_0_5px,var(--color-card)_5px_10px)]';

function selectionModeFrom(event: PointerEvent): SelectionMode {
  if (event.ctrlKey || event.metaKey) return 'append';
  if (event.shiftKey) return 'extend';
  return 'replace';
}

function keyboardMode(event: KeyboardEvent): SelectionMode {
  if (event.ctrlKey || event.metaKey) return 'append';
  if (event.shiftKey) return 'extend';
  return 'replace';
}

function RangeChip({ index }: { index: number }) {
  return (
    <span className="bg-forest rounded-pill absolute left-1/2 top-1 z-30 -translate-x-1/2 whitespace-nowrap px-2 py-px text-[10px] font-bold leading-[1.45] text-white">
      Zeitraum {index + 1}
    </span>
  );
}
